import React from 'react';
import { FigureCard } from './FigureCard';
interface Sys {
  name: string;
  t: string;
  realPct: number; // for display string
  realText: string;
  carnotPct: number;
  isModel: boolean;
  accent: string;
}
const systems: Sys[] = [{
  name: 'This experiment (60 °C)',
  t: '60 °C',
  realPct: 6.7e-8,
  realText: '6.7 × 10⁻⁸ %',
  carnotPct: 10.5,
  isModel: true,
  accent: '#1A4A68'
}, {
  name: 'This experiment (100 °C)',
  t: '100 °C',
  realPct: 1.3e-7,
  realText: '1.3 × 10⁻⁷ %',
  carnotPct: 20.1,
  isModel: true,
  accent: '#2E6B8E'
}, {
  name: 'Binary cycle — Iceland',
  t: '120 °C',
  realPct: 8,
  realText: '8 %',
  carnotPct: 29.0,
  isModel: false,
  accent: '#2E5E28'
}, {
  name: 'Flash steam — New Zealand',
  t: '200 °C',
  realPct: 15,
  realText: '15 %',
  carnotPct: 40.0,
  isModel: false,
  accent: '#EF9F27'
}, {
  name: 'Dry steam — The Geysers, CA',
  t: '230 °C',
  realPct: 21,
  realText: '21 %',
  carnotPct: 43.0,
  isModel: false,
  accent: '#8B2500'
}];
// Log scale: positions on a 0..1 visual axis spanning 1e-8 % to 50 %
const logMin = Math.log10(1e-8);
const logMax = Math.log10(50);
const toPos = (v: number) => (Math.log10(v) - logMin) / (logMax - logMin);
const ticks = [1e-8, 1e-6, 1e-4, 1e-2, 1, 10, 50];
const tickLabels: Record<string, string> = {
  '1e-8': '10⁻⁸ %',
  '0.000001': '10⁻⁶ %',
  '0.0001': '10⁻⁴ %',
  '0.01': '10⁻² %',
  '1': '1 %',
  '10': '10 %',
  '50': '50 %'
};
const fmtTick = (v: number) => v === 1e-8 ? '10⁻⁸' : v === 1e-6 ? '10⁻⁶' : v === 1e-4 ? '10⁻⁴' : v === 1e-2 ? '10⁻²' : v === 1 ? '1' : v === 10 ? '10' : v === 50 ? '50' : `${v}`;
export function Figure8() {
  return <FigureCard number={8} title="Laboratory Model vs Real-World Geothermal Plant Efficiencies" subtitle="η_Carnot (theoretical max) shown as outlined bars; η_real (actual) shown as solid bars. Source: IRENA (2023); U.S. DOE (2023)." caption="The laboratory model and industrial plants share the same thermodynamic principles, but differ by ~7–8 orders of magnitude in real efficiency due to engineering scale: higher T_hot, matched working fluid, insulated piping, and impedance-matched generators in industrial systems." data-id="element-376">
      <div className="bg-white" data-id="element-377">
        {/* X-axis label */}
        <div className="px-2 pb-2 font-sans text-[10px] uppercase tracking-wider text-[#5B5448] font-semibold flex justify-between" data-id="element-378">
          <span data-id="element-379">Real Efficiency, η_real (%) — log scale →</span>
          <span className="italic font-normal text-[#5B5448]/80" data-id="element-380">
            η_Carnot shown as outlined extension
          </span>
        </div>

        {/* Bar rows */}
        <div className="space-y-3" data-id="element-381">
          {systems.map(s => {
          const realPos = Math.max(0, Math.min(1, toPos(s.realPct)));
          const carnotPos = Math.max(0, Math.min(1, toPos(s.carnotPct)));
          return <div key={s.name} className="grid grid-cols-[260px_1fr_120px] items-center gap-3" data-id="element-382">
                {/* Label */}
                <div className="text-right" data-id="element-383">
                  <div className="font-serif text-[13px] font-semibold text-[#1B1B1B] leading-tight" data-id="element-384">
                    {s.name}
                  </div>
                  <div className="font-sans text-[10px] text-[#5B5448]" data-id="element-385">
                    T_hot = {s.t}
                  </div>
                </div>

                {/* Bar track */}
                <div className="relative h-10 bg-[#FBFAF6] border border-[#E5E0D6] rounded-sm overflow-hidden" data-id="element-386">
                  {/* Vertical grid ticks */}
                  {ticks.map(v => <div key={v} className="absolute top-0 bottom-0 border-l border-dashed border-[#E5E0D6]" style={{
                left: `${toPos(v) * 100}%`
              }} data-id="element-387" />)}

                  {/* η_Carnot outlined bar */}
                  <div className="absolute top-1 bottom-1 border-2 border-dashed rounded-sm" style={{
                left: 0,
                width: `${carnotPos * 100}%`,
                borderColor: s.accent,
                background: 'transparent'
              }} data-id="element-388" />
                  {/* η_real solid bar */}
                  <div className={`absolute top-2 bottom-2 rounded-sm ${s.isModel ? 'hatched' : ''}`} style={{
                left: 0,
                width: `${realPos * 100}%`,
                background: s.isModel ? undefined : s.accent
              }} data-id="element-389" />
                  {/* η_Carnot end marker */}
                  <div className="absolute top-0 bottom-0 w-px" style={{
                left: `${carnotPos * 100}%`,
                background: s.accent
              }} data-id="element-390" />
                  <div className="absolute -top-0.5 font-mono text-[10px] font-semibold whitespace-nowrap" style={{
                left: `calc(${carnotPos * 100}% + 4px)`,
                color: s.accent
              }} data-id="element-391">
                    η_Carnot = {s.carnotPct}%
                  </div>
                </div>

                {/* η_real value */}
                <div className="text-left" data-id="element-392">
                  <div className="font-mono text-[12px] font-bold" style={{
                color: s.accent
              }} data-id="element-393">
                    {s.realText}
                  </div>
                  <div className="font-sans text-[9px] text-[#5B5448] uppercase tracking-wider" data-id="element-394">
                    η_real
                  </div>
                </div>
              </div>;
        })}
        </div>

        {/* X-axis ticks */}
        <div className="grid grid-cols-[260px_1fr_120px] gap-3 mt-2" data-id="element-395">
          <div data-id="element-396" />
          <div className="relative h-6" data-id="element-397">
            {ticks.map(v => <div key={v} className="absolute top-0" style={{
            left: `${toPos(v) * 100}%`,
            transform: 'translateX(-50%)'
          }} data-id="element-398">
                <div className="w-px h-2 bg-[#9B9384] mx-auto" data-id="element-399" />
                <div className="font-mono text-[10px] text-[#374151] mt-0.5 whitespace-nowrap" data-id="element-400">
                  {fmtTick(v)}
                  {(v === 1 || v === 10 || v === 50) && ''}
                  {v < 1 && '%'}
                  {v >= 1 && ' %'}
                </div>
              </div>)}
          </div>
          <div data-id="element-401" />
        </div>

        {/* Connection annotation */}
        <div className="mt-6 bg-[#FEF3C7] border-2 border-[#EF9F27] rounded-sm p-4 flex items-center gap-4" data-id="element-402">
          <div className="text-[#B45309] font-bold text-lg" data-id="element-403">↔</div>
          <div className="font-serif text-[13px] text-[#1B1B1B]" data-id="element-404">
            <span className="font-semibold" data-id="element-405">
              Same thermodynamic principles — different scale and engineering.
            </span>{' '}
            The model demonstrates the same Carnot ceiling and trapezoidal-rule
            energy accounting that govern multi-megawatt plants; the gap is
            closed only by raising T_hot, insulating the steam path, and
            matching electrical impedance.
          </div>
        </div>

        {/* Why the gap */}
        <div className="mt-4 grid grid-cols-2 gap-3" data-id="element-406">
          <div className="border border-[#E5E0D6] rounded-sm p-4" data-id="element-407">
            <div className="font-sans text-[10px] uppercase tracking-wider text-[#8B2500] font-semibold mb-2" data-id="element-408">
              Laboratory model — losses
            </div>
            <ul className="font-serif text-[12px] text-[#1B1B1B] space-y-1 list-disc pl-5" data-id="element-409">
              <li data-id="element-410">Incomplete vaporisation of water reservoir</li>
              <li data-id="element-411">Uninsulated tubing (radiative + conductive loss)</li>
              <li data-id="element-412">Severe impedance mismatch (100 kΩ vs ~10–100 Ω)</li>
              <li data-id="element-413">Friction-dominated low-torque motor</li>
            </ul>
          </div>
          <div className="border border-[#E5E0D6] rounded-sm p-4" data-id="element-414">
            <div className="font-sans text-[10px] uppercase tracking-wider text-[#2E5E28] font-semibold mb-2" data-id="element-415">
              Industrial solution
            </div>
            <ul className="font-serif text-[12px] text-[#1B1B1B] space-y-1 list-disc pl-5" data-id="element-416">
              <li data-id="element-417">High T_hot (120–230 °C) via geothermal reservoirs</li>
              <li data-id="element-418">Matched working fluid (isobutane / pentane for binary)</li>
              <li data-id="element-419">Optimised multi-stage turbine geometry</li>
              <li data-id="element-420">Grid-matched synchronous generator + transformer</li>
            </ul>
          </div>
        </div>

        {/* Reference line note + sources */}
        <div className="mt-4 flex items-center justify-between font-sans text-[10px] text-[#5B5448]" data-id="element-421">
          <span className="italic" data-id="element-422">
            Dashed outlined bars indicate η_Carnot ceiling at each T_hot · solid
            bars indicate measured η_real.
          </span>
          <span data-id="element-423">Sources: IRENA (2023); U.S. DOE (2023).</span>
        </div>
      </div>
    </FigureCard>;
}